import React, { useMemo, useEffect, useState, useRef } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import { Button, Drawer, Modal, Radio, Table, message } from 'antd';
import range from 'lodash/range';
import { dayFormatType, formatDay } from '~helpers/day';
import { SearchOutlined } from '@ant-design/icons';
import {
  TableSearchInput,
  TableSearchDate,
  TableAsyncSelect,
} from 'common/components/table-filter-component';
import { sortDirectionMapping } from '~constants';
import {
  bookingJourneyType,
  allBookingStatus,
  landlordBookingStatus,
} from 'common/constants/booking';
import generatePath from '~settings/routing';
import BookingTable from '../components/table';
import { GET_MANUAL_BOOKING_LIST, LIST_STAFFUSERS } from '../queries';
import { handleExportToExcel } from '../utils/export-excel-manual';
import CommissionReport from '../components/commission-report';
import { QUERY_LANDLORD_NAMES } from '@pages/dashboard/landlord/queries';
import { titleCase } from 'common/helpers/titleCase';
import { convertEmptyArrayToNull } from 'common/helpers/convertEmptyArrayToNull';

export default function MessageOrManualProperty({ t }) {
  const history = useHistory();
  const [tableData, setTableData] = useState();
  const [filters, setFilters] = useState({});
  const [exportLoading, setExportLoading] = useState(false);
  const [visibleReport, setVisibleReport] = useState(false);
  const [visible, setVisible] = useState(false);
  const [checkedIndex, setCheckedIndex] = useState(0);
  const [searchBCName, setSearchBCName] = useState(null);
  const [landlordNamePageNumber, setLandlordNamePageNumber] = useState(1);
  const [query, setQuery] = useState('');
  const [landlordNames, setLandlordNames] = useState([]);

  const hasMore = useRef(false);

  const landlordNamePageSize = 20;

  const statusFilter = useMemo(() => {
    return Object.keys(allBookingStatus).map(status => ({
      text: t(`cms.booking.message_property.table.booking_status.${status.toLocaleLowerCase()}`),
      value: status,
    }));
  }, []);

  const marketFilter = useMemo(() => {
    let allStatus = {
      ME: 'ME',
      EUROPE: 'EUROPE',
      LATAM: 'LATAM',
      ROW: 'ROW',
      SEA: 'SEA',
      UK: 'UK',
      SA: 'SA',
      US: 'US',
      GC: 'GC',
      AUS: 'AUS',
      EAST_ASIA: 'EAST_ASIA',
    };
    return Object.keys(allStatus).map(status => ({
      text: allStatus[status],
      value: status,
    }));
  }, []);

  const { loading: BCListLoading, data: BCListResponse } = useQuery(LIST_STAFFUSERS, {
    variables: {
      name: searchBCName || null,
    },
  });

  const BCList = useMemo(() => {
    return (
      BCListResponse?.listStaffUsers?.edges.map(edge => ({
        label: edge.node.name,
        value: edge.node.id,
      })) || []
    );
  }, [BCListResponse]);

  const { data: landlordNameData, loading: isLandlordNamesLoading } = useQuery(
    QUERY_LANDLORD_NAMES,
    {
      variables: {
        query,
        pageNumber: landlordNamePageNumber,
        pageSize: landlordNamePageSize,
      },
      fetchPolicy: 'network-only',
      onError: e => {
        message.error(e?.toString());
      },
    },
  );

  const handleScrollEnd = () => {
    if (hasMore.current) {
      setLandlordNamePageNumber(prev => prev + 1);
    }
  };

  const handleSearch = value => {
    setQuery(prev => {
      if (prev !== value) {
        return value;
      }
    });
    setLandlordNamePageNumber(1);
    setLandlordNames([]);
    hasMore.current = true;
  };

  useEffect(() => {
    if (!isLandlordNamesLoading) {
      const result = landlordNameData.search.edges?.map(item => ({
        label: item.node?.name,
        value: item.node?.id,
      }));
      if (result.length < landlordNamePageSize) {
        hasMore.current = false;
      } else {
        hasMore.current = true;
      }
      if (result.length) {
        setLandlordNames(prev => [...prev, ...result]);
      }
    }
  }, [isLandlordNamesLoading]);

  const searchFilterAddon = key => {
    return {
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div className="partner__body__list__columns-dropdown">
          <TableSearchInput
            field={`${key}`}
            value={selectedKeys}
            onChange={(_fieldName, value) => {
              if (value) {
                setSelectedKeys(value);
                confirm();
              } else {
                clearFilters();
              }
            }}
          />
        </div>
      ),
      filterIcon: filtered => (
        <SearchOutlined style={{ color: filtered ? '#38b2a6' : undefined }} />
      ),
    };
  };
  const searchDateFilterAddon = key => {
    return {
      filterDropdown: ({ setSelectedKeys, confirm, clearFilters }) => (
        <TableSearchDate
          field={`search_${key}`}
          onChange={(_fieldName, value) => {
            if (value) {
              setSelectedKeys(value);
              confirm();
            } else {
              clearFilters();
            }
          }}
        />
      ),
      filterIcon: filtered => (
        <SearchOutlined style={{ color: filtered ? '#38b2a6' : undefined }} />
      ),
    };
  };
  const searchListAddon = key => {
    return {
      filterDropdown: ({ setSelectedKeys, confirm, clearFilters }) => (
        <TableAsyncSelect
          field={`search_${key}`}
          isMulti
          loading={BCListLoading}
          value={[]}
          list={BCList}
          onInput={value => {
            setSearchBCName(value);
          }}
          fetching={BCListLoading}
          onChange={(_fieldName, value) => {
            if (value) {
              setSelectedKeys(value);
              confirm();
            } else {
              clearFilters();
            }
          }}
        />
      ),
      filterIcon: filtered => (
        <SearchOutlined style={{ color: filtered ? '#38b2a6' : undefined }} />
      ),
    };
  };

  const searchPartnerAddon = key => {
    return {
      filterDropdown: ({ setSelectedKeys, confirm, clearFilters }) => (
        <TableAsyncSelect
          field={`search_${key}`}
          isMulti
          list={landlordNames}
          onInput={value => {
            handleSearch(value);
          }}
          fetching={isLandlordNamesLoading}
          onChange={(_fieldName, value) => {
            if (value && value.length !== 0) {
              setSelectedKeys(value);
              confirm();
            } else {
              setQuery('');
              clearFilters();
            }
          }}
          onScrollEnd={handleScrollEnd}
        />
      ),
      filterIcon: filtered => (
        <SearchOutlined style={{ color: filtered ? '#38b2a6' : undefined }} />
      ),
    };
  };

  const manualTableColumns = [
    {
      title: t('cms.booking.manual_property.table.student_name'),
      key: 'studentName',
      width: 160,
      fixed: 'left',
      render: (_, record) => (
        <div className="transfer__table-field">{`${record?.node?.student?.fullName} ` ?? '-'}</div>
      ),
      ...searchFilterAddon('studentName'),
    },
    {
      title: t('cms.booking.manual_property.table.internal_student_id'),
      key: 'studentInternalId',
      width: 180,
      render: (_, record) => (
        <span className="transfer__table-field">{record?.node?.student?.referenceId ?? '-'}</span>
      ),
      ...searchFilterAddon('studentInternalId'),
    },
    {
      title: t('cms.booking.manual_property.table.external_student_id'),
      key: 'studentExternalId',
      width: 180,
      render: (_, record) => (
        <span className="transfer__table-field">
          {record?.node?.student?.studentMapping?.externalId ?? '-'}
        </span>
      ),
      ...searchFilterAddon('studentExternalId'),
    },
    {
      title: t('cms.booking.manual_property.table.booking_number'),
      key: 'booking_number',
      width: 140,
      render: (_, record) => (
        <span className="transfer__table-field">{record?.node?.referenceId ?? '-'}</span>
      ),
    },
    {
      title: t('cms.booking.manual_property.table.phone_number'),
      key: 'studentRegisteredPhone',
      width: 150,
      render: (_, record) => (
        <span className="transfer__table-field">
          {record?.node?.student?.registeredPhone ?? '-'}
        </span>
      ),
      ...searchFilterAddon('studentRegisteredPhone'),
    },
    {
      title: t('cms.booking.manual_property.table.email'),
      key: 'studentEmail',
      width: 220,
      render: (_, record) => (
        <span className="transfer__table-field">{record?.node?.student?.emailAddress ?? '-'}</span>
      ),
      ...searchFilterAddon('studentEmail'),
    },
    {
      title: t('cms.booking.manual_property.table.source_region'),
      key: 'market',
      width: 140,
      render: (_, record) => (
        <span className="transfer__table-field">{record?.node?.market ?? '-'}</span>
      ),
      filters: marketFilter,
      filterMultiple: false,
    },
    {
      title: t('cms.booking.manual_property.table.partner_code'),
      key: 'partnerCode',
      width: 140,
      render: (_, record) => (
        <span className="transfer__table-field">{record?.node?.partnerCode ?? '-'}</span>
      ),
      ...searchFilterAddon('partnerCode'),
    },
    {
      title: t('cms.booking.manual_property.table.booking_status'),
      key: 'status',
      width: 180,
      filters: statusFilter,
      render: (_, record) => (
        <span className="transfer__table-field">
          {record?.node?.stage
            ? statusFilter.find(t => t.value === record?.node?.stage)?.text
            : '-'}
        </span>
      ),
    },
    {
      title: t('cms.booking.manual_property.table.complete_date'),
      key: 'complete_date',
      dataIndex: 'complete_date',
      width: 184,
      // sorter: tableData?.edges?.length > 0,
      render: (_, record) => (
        <span className="transfer__table-field">
          {record?.node?.completedAt
            ? formatDay(dayFormatType.UKWITHHM, record?.node?.completedAt)
            : '-'}
        </span>
      ),
      ...searchDateFilterAddon('complete_date'),
    },
    {
      title: t('cms.booking.manual_property.table.created_date'),
      key: 'created_date',
      width: 184,

      render: (_, record) => (
        <span className="transfer__table-field">
          {record?.node?.createdAt
            ? formatDay(dayFormatType.UKWITHHM, record?.node?.createdAt)
            : '-'}
        </span>
      ),
      ...searchDateFilterAddon('created_date'),
    },
    {
      title: t('cms.booking.manual_property.table.owner_bc'),
      key: 'owner_bc',
      width: 140,
      render: (_, record) => (
        <span className="transfer__table-field">{record?.node?.ownedByUser?.name ?? '-'}</span>
      ),
      ...searchListAddon('owner_bc'),
    },
    {
      title: t('cms.booking.manual_property.table.destination_university'),
      key: 'destinationUniversity',
      width: 240,
      render: (_, record) => (
        <span className="transfer__table-field">
          {record?.node?.destinationUniversity ?? t('cms.students.detail.field.default_university')}
        </span>
      ),
      ...searchFilterAddon('destinationUniversity'),
    },
    {
      title: t('cms.booking.manual_property.table.booked_landlord'),
      key: 'landlordIds',
      width: 180,
      render: (_, record) => (
        <span className="transfer__table-field">
          {record?.node?.interestedXbooking?.landlordName ?? '-'}
        </span>
      ),
      ...searchPartnerAddon('landlordIds'),
    },
    {
      title: t('cms.booking.manual_property.table.booked_property'),
      key: 'propertyName',
      width: 200,
      render: (_, record) => (
        <span className="transfer__table-field">
          {record?.node?.interestedXbooking?.propertyName ?? '-'}
        </span>
      ),
      ...searchFilterAddon('propertyName'),
    },
    {
      title: t('cms.booking.manual_property.table.booked_room_type'),
      key: 'booked_room_type',
      width: 240,
      render: (_, record) => (
        <span className="transfer__table-field">
          {titleCase(record?.node?.interestedXbooking?.roomType)}
        </span>
      ),
    },
    {
      title: t('cms.booking.manual_property.table.booked_move_in_date'),
      key: 'booked_move_in_date',
      width: 228,
      render: (_, record) => (
        <span className="transfer__table-field">
          {record?.node?.finalMoveInDate
            ? formatDay(dayFormatType.UKWITHHM, record?.node?.finalMoveInDate)
            : '-'}
        </span>
      ),
      ...searchDateFilterAddon('booked_move_in_date'),
    },
    {
      title: t('cms.booking.manual_property.table.booked_move_out_date'),
      key: 'booked_move_out_date',
      width: 228,
      render: (_, record) => (
        <span className="transfer__table-field">
          {record?.node?.finalMoveOutDate
            ? formatDay(dayFormatType.UKWITHHM, record?.node?.finalMoveOutDate)
            : '-'}
        </span>
      ),
      ...searchDateFilterAddon('booked_move_out_date'),
    },
    {
      title: t('cms.booking.manual_property.table.booked_tenancy_length'),
      key: 'booked_tenancy_length',
      width: 200,
      render: (_, record) => (
        <span className="transfer__table-field">
          {record?.node?.tenancyDurationDaysWeeks?.weeks
            ? record?.node?.tenancyDurationDaysWeeks?.weeks + ' ' + 'weeks'
            : '-'}
        </span>
      ),
    },

    {
      title: t('cms.booking.manual_property.table.landlord_booking_status'),
      key: 'landlordBookingStatuses',
      width: 240,
      render: (_, record) => (
        <span className="transfer__table-field">
          {landlordBookingStatus.find(item => item.value === record?.node?.landlordBookingStatus)
            ?.text ?? '-'}
        </span>
      ),
      filters: landlordBookingStatus,
    },
    {
      title: t('cms.booking.manual_property.table.final_price'),
      key: 'final_price',
      width: 140,
      render: (_, record) => (
        <span className="transfer__table-field">
          {record?.node?.finalPrice
            ? (record?.node?.currency ?? '') + record?.node?.finalPrice
            : '-'}
        </span>
      ),
    },
    {
      title: t('cms.booking.manual_property.table.total_commission_charged'),
      key: 'total_commission_charged',
      width: 240,
      render: (_, record) => (
        <span className="transfer__table-field">
          {record?.node?.interestedXbooking?.commission?.totalCommissionValue
            ? record?.node?.interestedXbooking?.commission?.currency +
              record?.node?.interestedXbooking?.commission?.totalCommissionValue
            : '-'}
        </span>
      ),
    },
    {
      title: t('cms.booking.manual_property.table.commission_converted_usd'),
      key: 'commission_converted_usd',
      width: 240,
      render: (_, record) => (
        <span className="transfer__table-field">
          {record?.node?.interestedXbooking?.commission?.commissionConvertedUsd ?? '-'}
        </span>
      ),
    },
    {
      title: t('cms.booking.manual_property.table.commission_tier_name'),
      key: 'commission_tier_name',
      width: 240,
      render: (_, record) => (
        <span className="transfer__table-field">
          {record?.node?.interestedXbooking?.commission?.data?.tier?.name ?? '-'}
        </span>
      ),
    },
    {
      title: t('cms.booking.manual_property.table.agent_service_fee'),
      key: 'agent_service_fee',
      width: 240,
      render: (_, record) => (
        <span className="transfer__table-field">{record?.node?.agentServiceFeeInRmb ?? '-'}</span>
      ),
    },
  ];

  const handleOnRowClick = record => {
    const url =
      record.node?.bookingJourney === bookingJourneyType.SEMI_AUTOMATIC
        ? generatePath('booking.detail', { id: record.node?.referenceId })
        : generatePath('booking.manualDetail', { id: record.node?.referenceId });
    history.push(url);
  };

  const [queryBookingData, { loading }] = useLazyQuery(GET_MANUAL_BOOKING_LIST, {
    fetchPolicy: 'no-cache',
    variables: {},
    onCompleted: data => {
      if (!exportLoading) {
        setTableData(data.listOpportunities);
      } else if (exportLoading && data.listOpportunities?.edges?.length > 0) {
        let exceldata = data.listOpportunities?.edges?.map((data, index) => {
          return {
            index: checkedIndex * 1000 + 1 + index,
            a: data?.node?.student?.fullName,
            b: data?.node?.referenceId,
            c: data?.node?.student?.studentMapping?.externalId ?? '-',
            d: data?.node?.referenceId ?? '-',
            e: data?.node?.student?.registeredPhone,
            f: data?.node?.student?.emailAddress,
            g: data?.node?.market,
            h: data?.node?.partnerCode ?? '-',
            i: data?.node?.stage
              ? statusFilter.find(t => t.value === data?.node?.stage)?.text
              : '-',
            j: data?.node?.completedAt
              ? formatDay(dayFormatType.UKWITHHM, data?.node?.completedAt)
              : '-',
            k: data?.node?.createdAt
              ? formatDay(dayFormatType.UKWITHHM, data?.node?.createdAt)
              : '-',
            l: data?.node?.ownedByUser?.name ?? '-',

            o: data?.node?.destinationUniversity ?? '-',
            p: data?.interestedXbooking?.landlordName ?? '-',
            q: data?.node?.interestedXbooking?.propertyName,
            r: titleCase(data?.node?.interestedXbooking?.roomType),
            s: data?.node?.finalMoveInDate
              ? formatDay(dayFormatType.UKWITHHM, data?.node?.finalMoveInDate)
              : '-',
            t: data?.node?.finalMoveOutDate
              ? formatDay(dayFormatType.UKWITHHM, data?.node?.finalMoveOutDate)
              : '-',
            u: data?.node?.tenancyDurationDaysWeeks?.weeks
              ? data?.node?.tenancyDurationDaysWeeks?.weeks + ' ' + 'weeks'
              : '-',
            v:
              landlordBookingStatus.find(item => item.value === data?.node?.landlordBookingStatus)
                ?.text ?? '-',
            w: data?.node?.finalPrice ? (data?.node?.currency ?? '') + data?.node?.finalPrice : '-',
            x: data?.node?.interestedXbooking?.commission?.totalCommissionValue
              ? data?.node?.interestedXbooking?.commission?.currency +
                data?.node?.interestedXbooking?.commission?.totalCommissionValue
              : '-',
            y: data?.node?.interestedXbooking?.commission?.commissionConvertedUsd ?? '-',
            z: data?.node?.interestedXbooking?.commission?.data?.tier?.name ?? '-',
            aa: data?.node?.agentServiceFeeInRmb ?? '-',
          };
        });
        handleExportToExcel(exceldata, () => {
          setExportLoading(false);
          setVisible(false);
        });
      } else {
        setExportLoading(false);
        setVisible(false);
      }
    },
  });

  const handleSort = (page, filter, sorter) => {
    const pageSize = page.pageSize !== filters?.pageSize ? page?.pageSize : filters?.pageSize;
    const pageNumber = filters?.pageSize && page.pageSize !== filters?.pageSize ? 1 : page.current;

    let params = {
      studentFullName: convertEmptyArrayToNull(filter.studentName),
      studentEmail: convertEmptyArrayToNull(filter.studentEmail),
      studentRegisteredPhone: convertEmptyArrayToNull(filter.studentRegisteredPhone),
      studentInternalId: convertEmptyArrayToNull(filter.studentInternalId),
      studentExternalId: convertEmptyArrayToNull(filter.studentExternalId),
      market: convertEmptyArrayToNull(filter.market),
      partnerCode: convertEmptyArrayToNull(filter.partnerCode),
      status: convertEmptyArrayToNull(filter.status),
      completedAtStart: filter?.complete_date?.length === 2 ? filter.complete_date[0] : null,
      completedAtEnd: filter?.complete_date?.length === 2 ? filter.complete_date[1] : null,
      createdAtStart: filter?.created_date?.length === 2 ? filter.created_date[0] : null,
      createdAtEnd: filter?.created_date?.length === 2 ? filter.created_date[1] : null,
      finalMoveInDateStart:
        filter?.booked_move_in_date?.length === 2 ? filter.booked_move_in_date[0] : null,
      finalMoveInDateEnd:
        filter?.booked_move_in_date?.length === 2 ? filter.booked_move_in_date[1] : null,
      finalMoveOutDateStart:
        filter?.booked_move_out_date?.length === 2 ? filter.booked_move_out_date[0] : null,
      finalMoveOutDateEnd:
        filter?.booked_move_out_date?.length === 2 ? filter.booked_move_out_date[1] : null,
      staffUserIds: convertEmptyArrayToNull(filter?.owner_bc),
      destinationUniversity: convertEmptyArrayToNull(filter.destinationUniversity),
      propertyName: convertEmptyArrayToNull(filter.propertyName),
      landlordBookingStatuses: filter.landlordBookingStatuses,
      landlordIds: filter.landlordIds,
    };

    const variables = {
      ...params,
      pageNumber: pageNumber ?? 1,
      pageSize: pageSize ?? 10,
      sortDirection: sortDirectionMapping[sorter.order],
    };

    setFilters(variables);
    queryBookingData({ variables });
  };

  const handleExport = () => {
    if (tableData.totalCount > 0) {
      setVisible(true);
    }
  };

  const exportDataModal = useMemo(() => {
    let modalData = [];
    if (tableData?.totalCount % 1000 === 0) {
      modalData = range(tableData?.totalCount / 1000 - 1);
    } else {
      modalData = range(tableData?.totalCount / 1000);
    }

    return modalData.map(item => ({
      value: item,
    }));
  }, [tableData?.totalCount]);

  const handleChecked = idx => {
    setCheckedIndex(idx);
  };

  const handleDownloadBooking = () => {
    setExportLoading(true);
    queryBookingData({
      variables: {
        ...filters,
        pageSize: 1000,
        pageNumber: checkedIndex + 1,
      },
    });
  };

  const getColumns = useMemo(
    () => [
      {
        title: '',
        dataIndex: 'value',
        key: 'value',
        render: (_, record) => {
          const value = record.value;
          return (
            <Radio
              checked={checkedIndex === value}
              className="radio-item"
              onClick={() => handleChecked(value)}
            >
              <span style={{ marginRight: 12 }}>{t('cms.sidebar.menu.booking')}</span>
              <span className="num-wrapper">{value * 1000 + 1}</span>
              <span className="dash">-</span>
              <span className="num-wrapper">
                {value === exportDataModal.length - 1 ? tableData?.totalCount : (value + 1) * 1000}
              </span>
            </Radio>
          );
        },
      },
    ],
    [checkedIndex, exportDataModal],
  );

  useEffect(() => {
    queryBookingData();
  }, []);

  return (
    <div style={{ position: 'relative' }}>
      <div className="booking__header-button">
        <Button icon="fund" type="link" onClick={() => setVisibleReport(true)}>
          {t('cms.booking.header.commission_report')}
        </Button>
        <Button icon="export" type="link" loading={exportLoading} onClick={handleExport}>
          {t('cms.reconciliation.landlord.export.export')}
        </Button>
      </div>
      <BookingTable
        columns={manualTableColumns}
        list={tableData}
        loading={loading && !exportLoading}
        filters={filters}
        handleTableChange={handleSort}
        onRowClick={handleOnRowClick}
        scroll={{ x: 1500 }}
      />
      <Drawer
        width={560}
        mask={false}
        destroyOnClose
        placement="right"
        onClose={() => setVisibleReport(false)}
        visible={visibleReport}
      >
        <CommissionReport t={t} />
      </Drawer>
      <Modal
        visible={visible}
        width={820}
        bodyStyle={{ padding: '32px 60px' }}
        okButtonProps={{ icon: 'download', loading: exportLoading }}
        okText="Download"
        onOk={handleDownloadBooking}
        onCancel={() => setVisible(false)}
        className="booking__modal-wrapper"
      >
        <header className="title">{t('cms.booking.export_data.title')}</header>
        <subtitle className="subtitle">{t('cms.booking.export_data.subtitle')}</subtitle>
        <section className="modal-content">
          <div className="export-header">
            <span className="available-export">{t('cms.booking.modal.header')}</span>
            <span className="bookings-count">
              {t('cms.booking.modal.export_count', { count: tableData?.totalCount })}
            </span>
          </div>
          <Table dataSource={exportDataModal} columns={getColumns} />
        </section>
      </Modal>
    </div>
  );
}
